import { useState } from 'react';
import { navigate } from '../../hooks/useHashRoute';
import { formatPercent, formatPrice, formatTime } from '../../lib/format';
import type { CandidateRecord, CandidateZoneSummary } from '../../types/market';
import { Badge } from '../ui/Badge';
import { Panel } from '../ui/Panel';
import { ScoreMeter } from '../ui/ScoreMeter';
import { DirectionBadge, FamilyBadge, StateBadge } from './CandidateBadges';
import { CandidateCard } from './CandidateCard';

interface CandidateTableProps {
  title: string;
  description: string;
  rows: CandidateRecord[];
  emptyMessage: string;
  mode: 'predictor' | 'monitoring';
  action?: React.ReactNode;
}

type ViewMode = 'grid' | 'table';

function percentClass(value: number): string {
  return value > 0 ? 'metric-positive' : value < 0 ? 'metric-negative' : 'metric-muted';
}

function zoneLabel(zone?: CandidateZoneSummary): string {
  if (!zone) return 'Pending';
  return `$${formatPrice(zone.low)}–$${formatPrice(zone.high)}`;
}

function openCandidate(row: CandidateRecord): void {
  navigate(`/coin/${row.symbol}`);
}

export function CandidateTable({ title, description, rows, emptyMessage, mode, action }: CandidateTableProps): React.JSX.Element {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  const viewSwitcher = (
    <div className="view-mode-switcher" aria-label="Candidate Layout Mode">
      <button
        type="button"
        className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
        onClick={() => setViewMode('grid')}
        title="Card Grid View"
      >
        <span className="btn-icon">⊞</span> Cards
      </button>
      <button
        type="button"
        className={`view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
        onClick={() => setViewMode('table')}
        title="Structured Table View"
      >
        <span className="btn-icon">≡</span> Table
      </button>
    </div>
  );

  return (
    <Panel
      title={title}
      description={description}
      action={
        <div className="panel-action-container">
          {viewSwitcher}
          {action}
        </div>
      }
    >
      {rows.length === 0 ? (
        <div className="table-empty">{emptyMessage}</div>
      ) : viewMode === 'grid' ? (
        <div className="candidate-card-grid">
          {rows.map((row) => (
            <CandidateCard key={row.id} candidate={row} mode={mode} />
          ))}
        </div>
      ) : (
        <div className="market-table-wrap">
          <table className="structured-market-table">
            <thead>
              <tr>
                <th>Asset & Direction</th>
                <th>Price & Delta</th>
                <th>Validation & Quality</th>
                <th>Levels & Clock</th>
                <th>Observed Market Driver</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            {rows.map((row) => {
              const validationTone = row.validationStatus === 'complete'
                ? 'positive'
                : row.validationStatus === 'partial'
                  ? 'info'
                  : row.validationStatus === 'failed'
                    ? 'negative'
                    : row.baselineReady
                      ? 'info'
                      : 'warning';

              return (
                <tbody
                  key={row.id}
                  className={`table-row-group row-group-${row.direction}`}
                >
                  {/* Tier 1: Primary Metric Row */}
                  <tr className="main-metric-row">
                    <td>
                      <a href={`#/coin/${row.symbol}`} className="table-row-link" aria-label={`Open ${row.symbol} deep analysis`} />
                      <div className="symbol-cell">
                        <span className="coin-avatar">{row.baseAsset.slice(0, 2)}</span>
                        <div className="symbol-copy">
                          <div className="asset-title-row">
                            <strong>{row.symbol}</strong>{row.predictionHeld && <Badge tone="info">held</Badge>}
                          </div>
                          <div className="symbol-meta-line">
                            <span>{row.primaryExchange ?? 'public'}</span>
                            <span>{row.marketKind}</span>
                            <span className="horizon-pill">{row.horizon}</span>
                          </div>
                          <div className="candidate-badge-stack">
                            <DirectionBadge direction={row.direction} />
                            <FamilyBadge family={row.family} />
                          </div>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="price-delta-cell">
                        <div className="price-row">
                          <span className="price-label">Current:</span>
                          <strong className="metric-value">${formatPrice(row.currentPrice)}</strong>
                        </div>
                        <div className="price-row">
                          <span className="price-label">Found:</span>
                          <span className="metric-value">${formatPrice(row.foundPrice)}</span>
                        </div>
                        <div className="delta-badge-row">
                          <span className="delta-tag-label">Change Since Found:</span>
                          <span className={`metric-badge ${percentClass(row.changeSinceFound)}`}>
                            {formatPercent(row.changeSinceFound)}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="quality-cell">
                        <div className="state-badge-row">
                          <StateBadge state={row.state} />
                          <Badge tone={validationTone}>{row.validationStatus}</Badge>
                        </div>
                        <ScoreMeter value={row.candidateScore} label={mode === 'predictor' ? 'Core score' : 'observation score'} />
                        <ScoreMeter value={row.opportunityFreshness} label="freshness" />
                        {row.predictionHeld && <span className="subrow-label">Prediction retained until horizon expiry · live radar update pending</span>}
                        {row.deepValidated && <span className="subrow-label">Independent support {row.supportingScore > 0 ? '+' : ''}{row.supportingScore} · {row.supportingAnalysis?.supportiveCount ?? 0} methods</span>}
                      </div>
                    </td>

                    <td>
                      <div className="levels-clock-cell">
                        <div className="sr-chip-row">
                          <span className="sr-tag sr-supp">S {zoneLabel(row.nearestSupport)}</span>
                          <span className="sr-tag sr-res">R {zoneLabel(row.nearestResistance)}</span>
                        </div>
                        <div className="clock-cell-info">
                          <strong>{row.candleMaturity.toFixed(0)}% mature</strong>
                          <span>
                            {row.freshIssuanceAllowed
                              ? row.secondsToClose < 60
                                ? `${Math.max(0, row.secondsToClose)}s to close`
                                : `${Math.ceil(row.secondsToClose / 60)}m to close`
                              : 'Transition block'}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="driver-cell">
                        <strong>{row.primaryDriver}</strong>
                        <p className="driver-summary-text">{row.contextSummary ?? row.statusNote}</p>
                      </div>
                    </td>

                    <td className="text-right">
                      <span className="subpage-action-btn">
                        Subpage <span className="arrow">→</span>
                      </span>
                    </td>
                  </tr>

                  {/* Tier 2: Integrated Sub-Row */}
                  <tr className="subrow-detail-line">
                    <td colSpan={6}>
                      <div className="subrow-content">
                        <div className="subrow-chip">
                          <span className="subrow-label">Found 24h:</span>
                          <span className={percentClass(row.foundChange24h)}>{formatPercent(row.foundChange24h)}</span>
                        </div>
                        <div className="subrow-chip">
                          <span className="subrow-label">Current 24h:</span>
                          <span className={percentClass(row.currentChange24h)}>{formatPercent(row.currentChange24h)}</span>
                        </div>
                        <div className="subrow-chip">
                          <span className="subrow-label">Best Thesis:</span>
                          <span className={percentClass(row.bestMove)}>{formatPercent(row.bestMove)}</span>
                        </div>
                        <div className="subrow-chip">
                          <span className="subrow-label">Worst Thesis:</span>
                          <span className={percentClass(row.worstMove)}>{formatPercent(row.worstMove)}</span>
                        </div>
                        <div className="subrow-chip">
                          <span className="subrow-label">Baseline:</span>
                          <span>{Math.round(row.sampleCoverageMs / 60_000)}m · {Math.round(row.validationCoverage * 100)}% deep</span>
                        </div>
                        <div className="subrow-chip subrow-chip-right">
                          <span className="subrow-label">Updated:</span>
                          <span>{formatTime(row.lastUpdatedAt)}</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              );
            })}
          </table>
        </div>
      )}
    </Panel>
  );
}
